import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const SEARCH_PARAM = 'q';
const INIT_ATTR = 'data-sr-collection-search-initialized';
const INPUT_DEBOUNCE_MS = 250;
const SEARCH_SECTION_ID = 'main';
const SEARCH_PATH = '/search';
const RESERVED_QUERY_KEYS = new Set([SEARCH_PARAM, 'page', 'section_id', 'sort_by']);
const SEARCH_ACTIVE_CLASS = 'sr-search-active';
const LOAD_MORE_LABEL = 'Pokaz wiecej';

/**
 * @param {HTMLFormElement} form
 */
function initCollectionSearch(form) {
  if (form.hasAttribute(INIT_ATTR)) return;
  form.setAttribute(INIT_ATTR, 'true');

  const input = form.querySelector('input[type="search"]');
  if (!(input instanceof HTMLInputElement)) return;

  const clearButton = form.querySelector('[data-sr-collection-search-clear]');
  const clearButtonElement = clearButton instanceof HTMLButtonElement ? clearButton : null;
  const hiddenInput = form.closest('results-list')?.querySelector('[data-sr-collection-search-hidden]');
  const hiddenInputElement = hiddenInput instanceof HTMLInputElement ? hiddenInput : null;
  let debounceTimer = /** @type {number | undefined} */ (undefined);
  let lastSubmitted = null;
  let lastRemoteQuery = null;
  let searchState = null;
  let searchPage = 1;
  let searchLastPage = 1;
  let loadMoreButton = null;
  let loadMoreInFlight = false;
  let collectionItemsCache = null;
  let collectionItemsCacheKey = null;
  let collectionItemsPromise = null;

  const getResultsList = () => form.closest('results-list');
  const getSectionId = () => getResultsList()?.getAttribute('section-id') ?? SEARCH_SECTION_ID;
  const getSearchSectionId = () => SEARCH_SECTION_ID;
  const getCollectionBaseUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(SEARCH_PARAM);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return url;
  };

  const setSearchActive = (active) => {
    const resultsList = getResultsList();
    if (!resultsList) return;
    resultsList.classList.toggle(SEARCH_ACTIVE_CLASS, active);
  };

  const getGridElement = () => document.querySelector('#ResultsList [ref="grid"]');

  const getLastPageValue = (root) => {
    const grid = root.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return 1;
    const lastPage = Number(grid.dataset.lastPage ?? '1');
    return Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 1;
  };

  const getCollectionLastPage = () => {
    if (searchState?.resultsHtml) {
      const fragment = new DOMParser().parseFromString(searchState.resultsHtml, 'text/html');
      return getLastPageValue(fragment);
    }
    return getLastPageValue(document);
  };

  const extractCollectionItems = (root) => {
    const grid = root.querySelector('#ResultsList [ref="grid"]');
    if (!(grid instanceof HTMLElement)) return [];
    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'))
      .filter((node) => node instanceof HTMLElement)
      .map((node) => ({
        html: node.outerHTML,
        search: (node.dataset.productSearch ?? '').toLowerCase(),
      }));
  };

  const loadAllCollectionItems = async () => {
    const baseUrl = getCollectionBaseUrl();
    const cacheKey = baseUrl.toString();

    if (collectionItemsCache && collectionItemsCacheKey === cacheKey) return collectionItemsCache;
    if (collectionItemsPromise && collectionItemsCacheKey === cacheKey) return collectionItemsPromise;

    collectionItemsCacheKey = cacheKey;
    collectionItemsPromise = (async () => {
      const totalPages = getCollectionLastPage();
      if (totalPages <= 1) {
        if (searchState?.resultsHtml) {
          const fragment = new DOMParser().parseFromString(searchState.resultsHtml, 'text/html');
          return extractCollectionItems(fragment);
        }
        return extractCollectionItems(document);
      }

      const items = [];
      for (let page = 1; page <= totalPages; page += 1) {
        const pageUrl = new URL(baseUrl.toString());
        if (page > 1) pageUrl.searchParams.set('page', String(page));
        const html = await sectionRenderer.getSectionHTML(getSectionId(), true, pageUrl);
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        items.push(...extractCollectionItems(fragment));
      }

      return items;
    })();

    try {
      collectionItemsCache = await collectionItemsPromise;
      return collectionItemsCache;
    } finally {
      collectionItemsPromise = null;
    }
  };

  const renderCollectionSearchResults = (items) => {
    const grid = getGridElement();
    if (!(grid instanceof HTMLElement)) return false;
    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = item.html;
      const node = wrapper.firstElementChild;
      if (node) fragment.appendChild(node);
    });

    grid.innerHTML = '';
    grid.appendChild(fragment);
    grid.dataset.lastPage = '1';

    const resultsRoot = document.querySelector('#ResultsList');
    if (resultsRoot) {
      resultsRoot
        .querySelectorAll('[ref="viewMorePrevious"], [ref="viewMoreNext"]')
        .forEach((node) => node.remove());
      resultsRoot.querySelectorAll('.pagination').forEach((node) => node.setAttribute('hidden', ''));
      const emptyState = resultsRoot.querySelector('[data-sr-collection-empty]');
      if (emptyState instanceof HTMLElement) {
        emptyState.toggleAttribute('hidden', items.length !== 0);
      }
    }

    return true;
  };

  const applyCollectionWideFilter = async (nextQuery) => {
    const query = (nextQuery ?? '').trim().toLowerCase();
    if (!query) return false;
    const allItems = await loadAllCollectionItems();
    if (!allItems || !allItems.length) return false;

    const matchedItems = allItems.filter((item) => item.search.includes(query));
    return renderCollectionSearchResults(matchedItems);
  };

  const updateLoadMoreButton = () => {
    if (!loadMoreButton) return;
    const shouldShow = searchLastPage > searchPage;
    loadMoreButton.hidden = !shouldShow;
    loadMoreButton.disabled = !shouldShow || loadMoreInFlight;
    loadMoreButton.textContent = loadMoreInFlight ? 'Ladowanie...' : LOAD_MORE_LABEL;
  };

  const removeLoadMoreButton = () => {
    if (!loadMoreButton) return;
    loadMoreButton.remove();
    loadMoreButton = null;
  };

  const ensureLoadMoreButton = () => {
    if (loadMoreButton && loadMoreButton.isConnected) return loadMoreButton;
    const resultsList = getResultsList();
    if (!resultsList) return null;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button sr-search-load-more';
    button.textContent = LOAD_MORE_LABEL;
    button.addEventListener('click', () => {
      loadMoreSearchResults();
    });
    const resultsRoot = document.querySelector('#ResultsList');
    if (resultsRoot?.parentNode) {
      resultsRoot.parentNode.insertBefore(button, resultsRoot.nextSibling);
    } else {
      resultsList.append(button);
    }
    loadMoreButton = button;
    updateLoadMoreButton();
    return button;
  };

  const captureState = () => {
    if (searchState) return;
    const resultsList = document.querySelector('#ResultsList');
    if (!resultsList) return;
    const count = document.querySelector('.products-count-wrapper');
    searchState = {
      resultsHtml: resultsList.outerHTML,
      countHtml: count ? count.innerHTML : null,
      url: window.location.href,
      scrollY: window.scrollY,
    };
    setSearchActive(true);
  };

  const resetSearchPagination = () => {
    searchPage = 1;
    searchLastPage = 1;
    loadMoreInFlight = false;
    removeLoadMoreButton();
  };

  const restoreState = () => {
    if (!searchState) return false;
    const currentResults = document.querySelector('#ResultsList');
    if (currentResults && searchState.resultsHtml) {
      const fragment = new DOMParser().parseFromString(searchState.resultsHtml, 'text/html');
      const restoredResults = fragment.querySelector('#ResultsList');
      if (restoredResults) {
        currentResults.replaceWith(restoredResults);
      }
    }

    const count = document.querySelector('.products-count-wrapper');
    if (count && searchState.countHtml != null) {
      count.innerHTML = searchState.countHtml;
    }

    setSearchActive(false);
    history.replaceState({}, '', searchState.url);

    const { scrollY } = searchState;
    searchState = null;
    resetSearchPagination();
    if (typeof scrollY === 'number') {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      });
    }

    return true;
  };

  const syncFromUrl = () => {
    const url = new URL(window.location.href);
    input.value = url.searchParams.get(SEARCH_PARAM) ?? '';
    if (hiddenInputElement) hiddenInputElement.value = input.value;
    updateClearButton();
    if (input.value.trim()) {
      setSearchActive(true);
      requestRemoteResults(input.value);
    } else {
      applyClientSideFilter('');
      setSearchActive(false);
    }
  };

  const updateClearButton = () => {
    if (!clearButtonElement) return;
    const hasValue = input.value.trim().length > 0;
    clearButtonElement.hidden = !hasValue;
  };

  const applyClientSideFilter = (nextQuery) => {
    const resultsList = form.closest('results-list');
    if (!resultsList) return;

    const query = (nextQuery ?? '').trim().toLowerCase();
    const items = resultsList.querySelectorAll('.product-grid__item');
    let visibleCount = 0;

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const haystack = (item.dataset.productSearch ?? '').toLowerCase();
      const isMatch = query.length === 0 || haystack.includes(query);
      item.hidden = !isMatch;
      if (isMatch) visibleCount += 1;
    });

    const emptyState = resultsList.querySelector('[data-sr-collection-empty]');
    if (emptyState instanceof HTMLElement) {
      const shouldShow = query.length > 0 && visibleCount === 0;
      emptyState.toggleAttribute('hidden', !shouldShow);
    }
  };

  const shouldForwardParam = (key) => {
    if (RESERVED_QUERY_KEYS.has(key)) return false;
    if (key.startsWith('filter.')) return false;
    if (key.startsWith('filter_')) return false;
    return true;
  };

  const buildSearchUrl = (query) => {
    const currentUrl = new URL(window.location.href);
    const searchUrl = new URL(SEARCH_PATH, window.location.origin);

    currentUrl.searchParams.forEach((value, key) => {
      if (!shouldForwardParam(key)) return;
      searchUrl.searchParams.append(key, value);
    });

    if (query) searchUrl.searchParams.set(SEARCH_PARAM, query);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('options[prefix]', 'last');

    return searchUrl;
  };

  const replaceResultsFromHTML = (html, { searchMode = false } = {}) => {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newResults = fragment.querySelector('#ResultsList');
    const currentResults = document.querySelector('#ResultsList');

    if (newResults && currentResults) {
      if (searchMode) {
        newResults
          .querySelectorAll('[ref="viewMorePrevious"], [ref="viewMoreNext"]')
          .forEach((node) => node.remove());
      }
      currentResults.replaceWith(newResults);
    }

    const newCount = fragment.querySelector('.products-count-wrapper');
    const currentCount = document.querySelector('.products-count-wrapper');
    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }

    if (searchMode && newResults) {
      searchPage = 1;
      searchLastPage = getLastPageValue(newResults);
      ensureLoadMoreButton();
      updateLoadMoreButton();
    }

    return Boolean(newResults && currentResults);
  };

  const appendResultsFromHTML = (html) => {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const nextGrid = fragment.querySelector('#ResultsList [ref="grid"]');
    const currentGrid = getGridElement();

    if (!nextGrid || !currentGrid) return false;
    const nextItems = nextGrid.querySelectorAll(':scope > [ref="cards[]"]');
    if (!nextItems.length) return false;
    currentGrid.append(...nextItems);
    return true;
  };

  const loadMoreSearchResults = async () => {
    const query = (lastRemoteQuery ?? '').trim();
    if (!query) return;
    if (loadMoreInFlight) return;
    if (searchPage >= searchLastPage) return;

    loadMoreInFlight = true;
    updateLoadMoreButton();
    const nextPage = searchPage + 1;

    try {
      const searchUrl = buildSearchUrl(query);
      searchUrl.searchParams.set('page', String(nextPage));
      const html = await sectionRenderer.getSectionHTML(getSearchSectionId(), false, searchUrl);
      if (lastRemoteQuery !== query) return;
      if (appendResultsFromHTML(html)) {
        searchPage = nextPage;
      }
    } finally {
      loadMoreInFlight = false;
      updateLoadMoreButton();
    }
  };

  const requestRemoteResults = async (nextQuery) => {
    const query = (nextQuery ?? '').trim();
    if (!query) {
      lastRemoteQuery = null;
      return;
    }
    if (lastRemoteQuery === query) return;
    lastRemoteQuery = query;

    captureState();
    if (await applyCollectionWideFilter(query)) return;

    try {
      const searchUrl = buildSearchUrl(query);
      const html = await sectionRenderer.getSectionHTML(getSearchSectionId(), false, searchUrl);
      if (lastRemoteQuery !== query) return;

      if (replaceResultsFromHTML(html, { searchMode: true })) return;
    } catch {
      applyClientSideFilter(query);
    }
  };

  const updateResults = (nextQuery) => {
    const url = new URL(window.location.href);
    const q = (nextQuery ?? '').trim();

    if (lastSubmitted === q) return;
    lastSubmitted = q;

    if (q) url.searchParams.set(SEARCH_PARAM, q);
    else url.searchParams.delete(SEARCH_PARAM);

    url.searchParams.delete('page');

    if (hiddenInputElement) hiddenInputElement.value = q;
    if (q) {
      requestRemoteResults(q);
    }

    const resultsList = form.closest('results-list');
    const facetsForm = resultsList?.querySelector('facets-form-component');

    if (!q && restoreState()) return;

    if (!q && facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(url.toString());
      return;
    }

    if (q) {
      history.pushState({ q }, '', url.toString());
      return;
    }

    window.location.assign(url.toString());
  };

  const scheduleUpdate = () => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      updateResults(input.value);
    }, INPUT_DEBOUNCE_MS);
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    updateResults(input.value);
  });

  input.addEventListener(
    'input',
    () => {
      updateClearButton();
      scheduleUpdate();
    },
    { passive: true }
  );

  if (clearButtonElement) {
    clearButtonElement.addEventListener('click', () => {
      input.value = '';
      updateClearButton();
      window.clearTimeout(debounceTimer);
      updateResults('');
      input.focus();
    });
  }

  window.addEventListener('popstate', () => {
    lastSubmitted = null;
    syncFromUrl();
  });

  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    collectionItemsCache = null;
    collectionItemsCacheKey = null;
    collectionItemsPromise = null;
  });

  syncFromUrl();
}

function initAll() {
  document
    .querySelectorAll(`[data-sr-collection-search]:not([${INIT_ATTR}])`)
    .forEach((node) => {
      if (node instanceof HTMLFormElement) initCollectionSearch(node);
    });
}

document.addEventListener('DOMContentLoaded', initAll);
initAll();

const observer = new MutationObserver(() => {
  initAll();
});

observer.observe(document.documentElement, {
  subtree: true,
  childList: true,
});
